export type EnergyMode = 'evidence' | 'senior';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const ENERGY_CALORIMETRY_TOOL_IDENTITY = {
  id: 'energy-calorimetry',
  toolKey: 'chemistry-energy-calorimetry',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

/** CT-MODE: modes in ascending start Year (study-chemistry-tools-prd.md §8.1). */
export const ENERGY_MODES: readonly { mode: EnergyMode; fromYear: number }[] = [
  { mode: 'evidence', fromYear: 8 },
  { mode: 'senior', fromYear: 11 },
];

export type ReviewStatus = 'pending-teaching-review';
const REVIEW_STATUS: ReviewStatus = 'pending-teaching-review';

export function reviewed(sources: DataSource[]) {
  return { sources, reviewStatus: REVIEW_STATUS };
}

export interface DataSource {
  label: string;
  detail: string;
}

export const ENERGY_SOURCES: Record<'nist' | 'crc' | 'derived' | 'model', DataSource> = {
  nist: {
    label: 'NIST Chemistry WebBook (SRD 69)',
    detail:
      'Standard enthalpies of combustion and formation at 298.15 K and 1 bar, product water as a liquid.',
  },
  crc: {
    label: 'CRC Handbook of Chemistry and Physics',
    detail: 'Enthalpies of solution of electrolytes at 25 °C (infinite dilution).',
  },
  derived: {
    label: 'Derived from NIST/CODATA enthalpies of formation',
    detail:
      'Aqueous-ion values combined by Hess’s law, e.g. H⁺(aq) + OH⁻(aq) → H₂O(l) = −55.8 kJ mol⁻¹.',
  },
  model: {
    label: 'Airbotix teaching model',
    detail:
      'Thermometer readings are illustrative classroom-scale values chosen to match the reviewed direction and magnitude. They are not measured data.',
  },
};

export const STANDARD_CONDITIONS =
  'Reference values are standard enthalpy changes at 25 °C (298 K) and 100 kPa, with water as a liquid product.';

/** Specific heat capacity of liquid water, J g⁻¹ K⁻¹. */
export const WATER_SPECIFIC_HEAT_J_PER_G_K = 4.18;
/** Assumed density of water and dilute aqueous solutions, g mL⁻¹. */
export const SOLUTION_DENSITY_G_PER_ML = 1.0;
const JOULES_PER_KILOJOULE = 1000;

// ---------------------------------------------------------------------------
// Evidence mode (Year 8): exothermic or endothermic?
// ---------------------------------------------------------------------------

export type EnergyDirection = 'exothermic' | 'endothermic';
export type ChangeType = 'chemical' | 'physical';

export interface ThermometerEvidence {
  kind: 'thermometer';
  /** What the thermometer is measuring. */
  location: string;
  startC: number;
  endC: number;
  /** Optional fair-test control reading. */
  control?: { label: string; startC: number; endC: number };
}

export interface EnergyInputEvidence {
  kind: 'energy-input';
  source: string;
  observation: string;
  /** Why a thermometer reading would mislead here. */
  thermometerTrap: string;
}

export interface EvidenceCase {
  id: string;
  name: string;
  equation: string;
  changeType: ChangeType;
  direction: EnergyDirection;
  evidence: ThermometerEvidence | EnergyInputEvidence;
  observation: string;
  explanation: string;
  /** Specific hint shown when the learner predicts the wrong direction. */
  directionHint: string;
  /** Specific hint shown when the learner chooses the wrong change type. */
  changeTypeHint: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

const evidenceSources = [ENERGY_SOURCES.model];

const EVIDENCE_CASE_DATA: Omit<EvidenceCase, 'sources' | 'reviewStatus'>[] = [
  {
    id: 'magnesium-hydrochloric-acid',
    name: 'Magnesium ribbon + hydrochloric acid',
    equation: 'Mg(s) + 2HCl(aq) → MgCl₂(aq) + H₂(g)',
    changeType: 'chemical',
    direction: 'exothermic',
    evidence: { kind: 'thermometer', location: 'the acid in the test tube', startC: 21.0, endC: 34.5 },
    observation: 'Fizzing, the ribbon disappears and the test tube feels warm.',
    explanation:
      'The reaction releases energy to its surroundings. The acid and the glass warm up, so the thermometer rises.',
    directionHint:
      'Look at the thermometer: it rose. Energy moved out of the reacting chemicals into the water, so the reaction is exothermic.',
    changeTypeHint:
      'Hydrogen gas and magnesium chloride are new substances. Fizzing from a new gas is evidence of a chemical reaction.',
  },
  {
    id: 'citric-acid-bicarbonate',
    name: 'Citric acid + sodium hydrogen carbonate',
    equation: 'C₆H₈O₇(aq) + 3NaHCO₃(s) → Na₃C₆H₅O₇(aq) + 3H₂O(l) + 3CO₂(g)',
    changeType: 'chemical',
    direction: 'endothermic',
    evidence: { kind: 'thermometer', location: 'the solution in the cup', startC: 21.0, endC: 13.5 },
    observation: 'Strong fizzing, and the cup feels cold.',
    explanation:
      'The reaction takes in energy from the surrounding water. The water loses energy, so the thermometer falls.',
    directionHint:
      'Fizzing does not mean heat is released. The thermometer fell, so the reacting chemicals took energy in from the water: endothermic.',
    changeTypeHint:
      'Carbon dioxide gas and sodium citrate are new substances, so this is a chemical reaction, not just dissolving.',
  },
  {
    id: 'ammonium-nitrate-dissolving',
    name: 'Ammonium nitrate dissolving in water',
    equation: 'NH₄NO₃(s) → NH₄⁺(aq) + NO₃⁻(aq)',
    changeType: 'physical',
    direction: 'endothermic',
    evidence: { kind: 'thermometer', location: 'the water in the beaker', startC: 21.0, endC: 11.0 },
    observation: 'The crystals dissolve to a clear, colourless solution that feels cold.',
    explanation:
      'Separating the ions takes in more energy than is released when water surrounds them, so energy is drawn from the water. Instant cold packs use this.',
    directionHint:
      'The thermometer fell by 10 °C. Dissolving this salt takes energy in from the water, so the process is endothermic.',
    changeTypeHint:
      'No new substance forms: evaporating the water gives back ammonium nitrate. Dissolving is treated as a physical change, even though it has an energy change.',
  },
  {
    id: 'calcium-chloride-dissolving',
    name: 'Calcium chloride dissolving in water',
    equation: 'CaCl₂(s) → Ca²⁺(aq) + 2Cl⁻(aq)',
    changeType: 'physical',
    direction: 'exothermic',
    evidence: { kind: 'thermometer', location: 'the water in the beaker', startC: 21.0, endC: 38.0 },
    observation: 'The solid dissolves to a clear solution and the beaker becomes warm.',
    explanation:
      'More energy is released as water molecules attract the ions than is needed to separate them, so the water warms. Some hot packs use this.',
    directionHint:
      'The thermometer rose by 17 °C. Energy was released into the water, so this dissolving is exothermic.',
    changeTypeHint:
      'Calcium chloride can be recovered by evaporating the water. No new substance forms, so this is a physical change.',
  },
  {
    id: 'ethanol-combustion',
    name: 'Ethanol burning in a spirit burner',
    equation: 'C₂H₅OH(l) + 3O₂(g) → 2CO₂(g) + 3H₂O(g)',
    changeType: 'chemical',
    direction: 'exothermic',
    evidence: {
      kind: 'thermometer',
      location: 'the water in the can above the flame',
      startC: 21.0,
      endC: 43.0,
    },
    observation: 'A blue flame, and the water above it warms steadily.',
    explanation:
      'Combustion releases energy as heat and light. A match is needed only to start it: once alight, the flame supplies its own activation energy.',
    directionHint:
      'Needing a match to start does not make a reaction endothermic. The water warmed by 22 °C, so burning releases energy: exothermic.',
    changeTypeHint:
      'Ethanol and oxygen become carbon dioxide and water, which are new substances. Burning is a chemical reaction.',
  },
  {
    id: 'neutralisation',
    name: 'Hydrochloric acid + sodium hydroxide',
    equation: 'HCl(aq) + NaOH(aq) → NaCl(aq) + H₂O(l)',
    changeType: 'chemical',
    direction: 'exothermic',
    evidence: { kind: 'thermometer', location: 'the mixed solutions', startC: 21.0, endC: 27.6 },
    observation: 'No colour change or fizzing, but the mixture warms.',
    explanation:
      'Hydrogen ions and hydroxide ions combine to form water and release energy. Neutralisation is exothermic.',
    directionHint:
      'With no fizzing or colour change, the thermometer is the only clue: it rose by 6.6 °C, so neutralisation is exothermic.',
    changeTypeHint:
      'Water forms from H⁺ and OH⁻ ions, so a new substance is made. The temperature change is evidence of the reaction.',
  },
  {
    id: 'copper-carbonate-decomposition',
    name: 'Heating copper(II) carbonate',
    equation: 'CuCO₃(s) → CuO(s) + CO₂(g)',
    changeType: 'chemical',
    direction: 'endothermic',
    evidence: {
      kind: 'energy-input',
      source: 'a Bunsen burner',
      observation:
        'The green powder turns black and the gas turns limewater milky only while it is heated. Remove the flame and the change stops.',
      thermometerTrap:
        'A thermometer in the test tube would rise because the burner is heating it. That rise comes from the flame, not from the reaction.',
    },
    observation: 'Green powder turns black; the gas given off turns limewater milky.',
    explanation:
      'Thermal decomposition needs a continuous supply of energy to break up the carbonate. A reaction that stops when the energy supply stops is endothermic.',
    directionHint:
      'The tube gets hot because of the flame, not the reaction. The change stops as soon as heating stops, so the reaction takes energy in: endothermic.',
    changeTypeHint:
      'Copper(II) oxide (black) and carbon dioxide are new substances, so this is a chemical reaction.',
  },
  {
    id: 'photosynthesis',
    name: 'Photosynthesis in pondweed',
    equation: '6CO₂(g) + 6H₂O(l) → C₆H₁₂O₆(aq) + 6O₂(g)',
    changeType: 'chemical',
    direction: 'endothermic',
    evidence: {
      kind: 'energy-input',
      source: 'light',
      observation:
        'Oxygen bubbles rise from the pondweed only while the lamp is on. In the dark the bubbling stops.',
      thermometerTrap:
        'The water temperature barely changes and the lamp also warms it, so a thermometer is not useful evidence here.',
    },
    observation: 'Bubbles of oxygen form while the plant is lit.',
    explanation:
      'Plants absorb light energy and store it as chemical energy in glucose. Photosynthesis is endothermic.',
    directionHint:
      'Photosynthesis only happens while light energy is supplied, and that energy is stored in glucose. It takes energy in: endothermic.',
    changeTypeHint:
      'Carbon dioxide and water become glucose and oxygen, which are new substances: a chemical reaction.',
  },
  {
    id: 'respiration',
    name: 'Respiration in germinating seeds',
    equation: 'C₆H₁₂O₆(aq) + 6O₂(g) → 6CO₂(g) + 6H₂O(l)',
    changeType: 'chemical',
    direction: 'exothermic',
    evidence: {
      kind: 'thermometer',
      location: 'germinating seeds in a vacuum flask, after 48 hours',
      startC: 20.0,
      endC: 23.5,
      control: { label: 'Boiled (dead) seeds', startC: 20.0, endC: 20.0 },
    },
    observation: 'The flask of living seeds slowly warms; the control flask does not.',
    explanation:
      'Cellular respiration releases energy from glucose. Some is transferred to the surroundings as heat, which the vacuum flask traps.',
    directionHint:
      'The living seeds warmed by 3.5 °C while the dead control stayed at 20.0 °C. Respiration releases energy: exothermic.',
    changeTypeHint:
      'Glucose and oxygen become carbon dioxide and water inside cells, so new substances form: a chemical reaction.',
  },
  {
    id: 'zinc-copper-sulfate',
    name: 'Zinc powder + copper(II) sulfate solution',
    equation: 'Zn(s) + CuSO₄(aq) → ZnSO₄(aq) + Cu(s)',
    changeType: 'chemical',
    direction: 'exothermic',
    evidence: { kind: 'thermometer', location: 'the solution in the cup', startC: 21.0, endC: 30.0 },
    observation: 'The blue colour fades, brown copper forms and the solution warms.',
    explanation:
      'Zinc displaces copper from solution and releases energy, so the solution warms.',
    directionHint:
      'The thermometer rose by 9 °C, so energy left the reacting chemicals and warmed the water: exothermic.',
    changeTypeHint:
      'Copper metal and zinc sulfate are new substances, and the blue colour fades. That is a chemical reaction.',
  },
];

export const EVIDENCE_CASES: EvidenceCase[] = EVIDENCE_CASE_DATA.map((entry) => ({
  ...entry,
  sources: evidenceSources,
  reviewStatus: REVIEW_STATUS,
}));

export const DIRECTION_LABELS: Record<EnergyDirection, string> = {
  exothermic: 'Exothermic — releases energy',
  endothermic: 'Endothermic — takes in energy',
};

export const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  chemical: 'Chemical reaction — new substances form',
  physical: 'Physical change — dissolving, no new substance',
};

export function temperatureChange(evidenceCase: EvidenceCase): number | null {
  if (evidenceCase.evidence.kind !== 'thermometer') return null;
  return roundTo(evidenceCase.evidence.endC - evidenceCase.evidence.startC, 1);
}

/** Direction the evidence implies; null when evidence is missing or contradictory. */
export function directionFromEvidence(evidenceCase: EvidenceCase): EnergyDirection | null {
  const { evidence } = evidenceCase;
  if (evidence.kind === 'energy-input') return 'endothermic';
  const change = evidence.endC - evidence.startC;
  if (!Number.isFinite(change) || change === 0) return null;
  return change > 0 ? 'exothermic' : 'endothermic';
}

export interface EvidenceCheck {
  directionCorrect: boolean;
  changeTypeCorrect: boolean;
  directionFeedback: string;
  changeTypeFeedback: string;
}

export function checkEvidencePrediction(
  evidenceCase: EvidenceCase,
  direction: EnergyDirection,
  changeType: ChangeType,
): EvidenceCheck {
  const directionCorrect = direction === evidenceCase.direction;
  const changeTypeCorrect = changeType === evidenceCase.changeType;
  return {
    directionCorrect,
    changeTypeCorrect,
    directionFeedback: directionCorrect
      ? `Correct: ${evidenceCase.explanation}`
      : evidenceCase.directionHint,
    changeTypeFeedback: changeTypeCorrect
      ? `Correct: ${CHANGE_TYPE_LABELS[evidenceCase.changeType].toLowerCase()}.`
      : evidenceCase.changeTypeHint,
  };
}

// ---------------------------------------------------------------------------
// Senior: calorimetry q = m c ΔT, ΔH = −q / n
// ---------------------------------------------------------------------------

export type CalorimetryKind = 'combustion' | 'neutralisation' | 'dissolution' | 'displacement';

export interface CalorimetryCase {
  id: string;
  name: string;
  kind: CalorimetryKind;
  equation: string;
  apparatus: string;
  /** Liquid heated or cooled, in mL. Mass = volume × 1.00 g mL⁻¹ (solid mass neglected). */
  liquidVolumeMl: number;
  /** For neutralisation: the volume of one solution, used to recognise a common mistake. */
  singleSolutionVolumeMl?: number;
  temperatureChangeK: number;
  /** How n was obtained, shown to the learner. */
  amountWorking: string;
  amountMol: number;
  /** Reviewed standard enthalpy change, kJ per mol of the named substance. */
  referenceDeltaHkJ: number;
  perMolOf: string;
  heatLossNote: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

const SPIRIT_BURNER_WATER_ML = 200;
const COFFEE_CUP_WATER_ML = 50;
const SPIRIT_BURNER_NOTE =
  'Much of the energy heats the air, the can and the burner, and some ethanol-family fuels burn incompletely (soot). The calculated |ΔH| is far smaller than the standard value.';
const COFFEE_CUP_NOTE =
  'A polystyrene cup with a lid loses little energy, so the result is close to the reference. The cup and thermometer still absorb a small amount.';

function burnerAmount(massG: number, molarMass: number): number {
  return massG / molarMass;
}

export const CALORIMETRY_CASES: CalorimetryCase[] = [
  {
    id: 'spirit-ethanol',
    name: 'Spirit burner: ethanol',
    kind: 'combustion',
    equation: 'C₂H₅OH(l) + 3O₂(g) → 2CO₂(g) + 3H₂O(l)',
    apparatus: 'Spirit burner under a copper can of water',
    liquidVolumeMl: SPIRIT_BURNER_WATER_ML,
    temperatureChangeK: 24.5,
    amountWorking: 'n = m / M = 1.20 g ÷ 46.07 g mol⁻¹',
    amountMol: burnerAmount(1.2, 46.07),
    referenceDeltaHkJ: -1367,
    perMolOf: 'ethanol',
    heatLossNote: SPIRIT_BURNER_NOTE,
    ...reviewed([ENERGY_SOURCES.nist, ENERGY_SOURCES.model]),
  },
  {
    id: 'spirit-methanol',
    name: 'Spirit burner: methanol',
    kind: 'combustion',
    equation: 'CH₃OH(l) + 1½O₂(g) → CO₂(g) + 2H₂O(l)',
    apparatus: 'Spirit burner under a copper can of water',
    liquidVolumeMl: SPIRIT_BURNER_WATER_ML,
    temperatureChangeK: 22.0,
    amountWorking: 'n = m / M = 1.50 g ÷ 32.04 g mol⁻¹',
    amountMol: burnerAmount(1.5, 32.04),
    referenceDeltaHkJ: -726,
    perMolOf: 'methanol',
    heatLossNote: SPIRIT_BURNER_NOTE,
    ...reviewed([ENERGY_SOURCES.nist, ENERGY_SOURCES.model]),
  },
  {
    id: 'spirit-propanol',
    name: 'Spirit burner: propan-1-ol',
    kind: 'combustion',
    equation: 'C₃H₇OH(l) + 4½O₂(g) → 3CO₂(g) + 4H₂O(l)',
    apparatus: 'Spirit burner under a copper can of water',
    liquidVolumeMl: SPIRIT_BURNER_WATER_ML,
    temperatureChangeK: 20.0,
    amountWorking: 'n = m / M = 1.00 g ÷ 60.10 g mol⁻¹',
    amountMol: burnerAmount(1.0, 60.1),
    referenceDeltaHkJ: -2021,
    perMolOf: 'propan-1-ol',
    heatLossNote: SPIRIT_BURNER_NOTE,
    ...reviewed([ENERGY_SOURCES.nist, ENERGY_SOURCES.model]),
  },
  {
    id: 'cup-hcl-naoh',
    name: 'Coffee cup: HCl + NaOH',
    kind: 'neutralisation',
    equation: 'HCl(aq) + NaOH(aq) → NaCl(aq) + H₂O(l)',
    apparatus: '50.0 mL of 1.00 mol L⁻¹ HCl + 50.0 mL of 1.00 mol L⁻¹ NaOH in a lidded cup',
    liquidVolumeMl: 100,
    singleSolutionVolumeMl: 50,
    temperatureChangeK: 6.6,
    amountWorking: 'n(H₂O) = c × V = 1.00 mol L⁻¹ × 0.0500 L',
    amountMol: 0.05,
    referenceDeltaHkJ: -55.8,
    perMolOf: 'water formed',
    heatLossNote: COFFEE_CUP_NOTE,
    ...reviewed([ENERGY_SOURCES.derived, ENERGY_SOURCES.model]),
  },
  {
    id: 'cup-hno3-koh',
    name: 'Coffee cup: HNO₃ + KOH',
    kind: 'neutralisation',
    equation: 'HNO₃(aq) + KOH(aq) → KNO₃(aq) + H₂O(l)',
    apparatus: '25.0 mL of 2.00 mol L⁻¹ HNO₃ + 25.0 mL of 2.00 mol L⁻¹ KOH in a lidded cup',
    liquidVolumeMl: 50,
    singleSolutionVolumeMl: 25,
    temperatureChangeK: 13.0,
    amountWorking: 'n(H₂O) = c × V = 2.00 mol L⁻¹ × 0.0250 L',
    amountMol: 0.05,
    referenceDeltaHkJ: -55.8,
    perMolOf: 'water formed',
    heatLossNote: COFFEE_CUP_NOTE,
    ...reviewed([ENERGY_SOURCES.derived, ENERGY_SOURCES.model]),
  },
  {
    id: 'cup-ammonium-nitrate',
    name: 'Dissolving ammonium nitrate',
    kind: 'dissolution',
    equation: 'NH₄NO₃(s) → NH₄⁺(aq) + NO₃⁻(aq)',
    apparatus: '5.00 g of NH₄NO₃ stirred into 50.0 mL of water in a lidded cup',
    liquidVolumeMl: COFFEE_CUP_WATER_ML,
    temperatureChangeK: -7.0,
    amountWorking: 'n = m / M = 5.00 g ÷ 80.04 g mol⁻¹',
    amountMol: 5 / 80.04,
    referenceDeltaHkJ: 25.7,
    perMolOf: 'NH₄NO₃',
    heatLossNote:
      'The cold solution gains some energy from the room, so the temperature fall and |ΔH| are slightly too small.',
    ...reviewed([ENERGY_SOURCES.crc, ENERGY_SOURCES.model]),
  },
  {
    id: 'cup-sodium-hydroxide',
    name: 'Dissolving sodium hydroxide',
    kind: 'dissolution',
    equation: 'NaOH(s) → Na⁺(aq) + OH⁻(aq)',
    apparatus: '2.00 g of NaOH pellets stirred into 50.0 mL of water in a lidded cup',
    liquidVolumeMl: COFFEE_CUP_WATER_ML,
    temperatureChangeK: 10.2,
    amountWorking: 'n = m / M = 2.00 g ÷ 40.00 g mol⁻¹',
    amountMol: 0.05,
    referenceDeltaHkJ: -44.5,
    perMolOf: 'NaOH',
    heatLossNote:
      'Some energy escapes before the maximum reading, and pellets absorb a little water from air before weighing.',
    ...reviewed([ENERGY_SOURCES.crc, ENERGY_SOURCES.model]),
  },
  {
    id: 'cup-potassium-nitrate',
    name: 'Dissolving potassium nitrate',
    kind: 'dissolution',
    equation: 'KNO₃(s) → K⁺(aq) + NO₃⁻(aq)',
    apparatus: '5.00 g of KNO₃ stirred into 50.0 mL of water in a lidded cup',
    liquidVolumeMl: COFFEE_CUP_WATER_ML,
    temperatureChangeK: -7.9,
    amountWorking: 'n = m / M = 5.00 g ÷ 101.10 g mol⁻¹',
    amountMol: 5 / 101.1,
    referenceDeltaHkJ: 34.9,
    perMolOf: 'KNO₃',
    heatLossNote:
      'The cold solution gains some energy from the room, so |ΔH| is slightly too small.',
    ...reviewed([ENERGY_SOURCES.crc, ENERGY_SOURCES.model]),
  },
  {
    id: 'cup-zinc-copper',
    name: 'Coffee cup: zinc + copper(II) sulfate',
    kind: 'displacement',
    equation: 'Zn(s) + Cu²⁺(aq) → Zn²⁺(aq) + Cu(s)',
    apparatus: 'Excess zinc powder added to 50.0 mL of 0.500 mol L⁻¹ CuSO₄ in a lidded cup',
    liquidVolumeMl: 50,
    temperatureChangeK: 24.5,
    amountWorking: 'n(Cu²⁺) = c × V = 0.500 mol L⁻¹ × 0.0500 L (zinc in excess)',
    amountMol: 0.025,
    referenceDeltaHkJ: -218.7,
    perMolOf: 'Cu²⁺ reacted',
    heatLossNote:
      'The reaction is not instant, so some energy escapes before the highest reading. Extrapolating a cooling curve back to the mixing time improves the result.',
    ...reviewed([ENERGY_SOURCES.derived, ENERGY_SOURCES.model]),
  },
];

export interface CalorimetryResult {
  massG: number;
  qJ: number;
  qkJ: number;
  deltaHkJPerMol: number;
  /** Experimental |ΔH| as a percentage of the reference |ΔH|. */
  percentOfReference: number;
}

export function calculateCalorimetry(calorimetryCase: CalorimetryCase): CalorimetryResult {
  const { liquidVolumeMl, temperatureChangeK, amountMol, referenceDeltaHkJ } = calorimetryCase;
  if (
    !(liquidVolumeMl > 0) ||
    !(amountMol > 0) ||
    !Number.isFinite(temperatureChangeK) ||
    temperatureChangeK === 0
  ) {
    throw new Error(`Unsupported calorimetry case: ${calorimetryCase.id}`);
  }
  const massG = liquidVolumeMl * SOLUTION_DENSITY_G_PER_ML;
  const qJ = massG * WATER_SPECIFIC_HEAT_J_PER_G_K * temperatureChangeK;
  const qkJ = qJ / JOULES_PER_KILOJOULE;
  const deltaHkJPerMol = -qkJ / amountMol;
  return {
    massG,
    qJ,
    qkJ,
    deltaHkJPerMol,
    percentOfReference: (Math.abs(deltaHkJPerMol) / Math.abs(referenceDeltaHkJ)) * 100,
  };
}

export type AnswerVerdict =
  | 'correct'
  | 'invalid'
  | 'sign'
  | 'units'
  | 'mass'
  | 'not-divided-by-n'
  | 'celsius'
  | 'other';

export interface AnswerCheck {
  verdict: AnswerVerdict;
  feedback: string;
}

const RELATIVE_TOLERANCE = 0.02;
const ABSOLUTE_TOLERANCE = 0.01;

/** Strict decimal parser: empty, partial or non-numeric input fails closed. */
export function parseNumericAnswer(raw: string): number | null {
  const cleaned = raw.trim().replace(/−/g, '-');
  if (!/^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function near(value: number, target: number): boolean {
  return Math.abs(value - target) <= Math.max(Math.abs(target) * RELATIVE_TOLERANCE, ABSOLUTE_TOLERANCE);
}

export function checkHeatAnswer(calorimetryCase: CalorimetryCase, raw: string): AnswerCheck {
  const value = parseNumericAnswer(raw);
  const result = calculateCalorimetry(calorimetryCase);
  if (value === null) {
    return { verdict: 'invalid', feedback: 'Enter q as a number in kJ, for example 2.76 or −1.46.' };
  }
  const expected = `q = ${formatSig(result.massG, 3)} g × ${WATER_SPECIFIC_HEAT_J_PER_G_K} J g⁻¹ K⁻¹ × ${formatDecimalSigned(calorimetryCase.temperatureChangeK, 1)} K = ${formatSigned(result.qkJ, 3)} kJ`;
  if (near(value, result.qkJ)) {
    return { verdict: 'correct', feedback: `Correct. ${expected}.` };
  }
  if (near(value, result.qJ)) {
    return {
      verdict: 'units',
      feedback: `That value is in joules. Divide by 1000 to give kJ. ${expected}.`,
    };
  }
  if (near(value, -result.qkJ)) {
    return {
      verdict: 'sign',
      feedback:
        result.qkJ > 0
          ? 'Check the sign. The water warmed (ΔT is positive), so the water gained energy and q is positive.'
          : 'Check the sign. The water cooled (ΔT is negative), so the water lost energy and q is negative.',
    };
  }
  if (calorimetryCase.singleSolutionVolumeMl !== undefined) {
    const singleMass = calorimetryCase.singleSolutionVolumeMl * SOLUTION_DENSITY_G_PER_ML;
    const singleQ = (singleMass * WATER_SPECIFIC_HEAT_J_PER_G_K * calorimetryCase.temperatureChangeK) / JOULES_PER_KILOJOULE;
    if (near(Math.abs(value), Math.abs(singleQ))) {
      return {
        verdict: 'mass',
        feedback: `Both solutions warm up. Use the total volume, ${calorimetryCase.liquidVolumeMl} mL, which is ${formatSig(result.massG, 3)} g at 1.00 g mL⁻¹.`,
      };
    }
  }
  return { verdict: 'other', feedback: `Not yet. ${expected}.` };
}

export function checkEnthalpyAnswer(calorimetryCase: CalorimetryCase, raw: string): AnswerCheck {
  const value = parseNumericAnswer(raw);
  const result = calculateCalorimetry(calorimetryCase);
  if (value === null) {
    return {
      verdict: 'invalid',
      feedback: 'Enter ΔH as a number in kJ mol⁻¹, including a sign, for example −55.2.',
    };
  }
  const expected = `ΔH = −q / n = −(${formatSigned(result.qkJ, 3)} kJ) ÷ ${formatSig(calorimetryCase.amountMol, 3)} mol = ${formatSigned(result.deltaHkJPerMol, 3)} kJ mol⁻¹`;
  if (near(value, result.deltaHkJPerMol)) {
    return { verdict: 'correct', feedback: `Correct. ${expected}.` };
  }
  if (near(value, -result.deltaHkJPerMol)) {
    return {
      verdict: 'sign',
      feedback:
        result.deltaHkJPerMol < 0
          ? 'The size is right but the sign is not. The surroundings gained energy, so the system lost it: ΔH is negative (exothermic).'
          : 'The size is right but the sign is not. The surroundings lost energy to the system, so ΔH is positive (endothermic).',
    };
  }
  if (
    near(value, result.deltaHkJPerMol * JOULES_PER_KILOJOULE) ||
    near(value, -result.deltaHkJPerMol * JOULES_PER_KILOJOULE)
  ) {
    return {
      verdict: 'units',
      feedback: `That is in J mol⁻¹. Convert q to kJ first. ${expected}.`,
    };
  }
  if (near(Math.abs(value), Math.abs(result.qkJ)) || near(Math.abs(value), Math.abs(result.qJ))) {
    return {
      verdict: 'not-divided-by-n',
      feedback: `That is the energy for this sample only. Divide by the amount reacted, n = ${formatSig(calorimetryCase.amountMol, 3)} mol, to get kJ per mole.`,
    };
  }
  return { verdict: 'other', feedback: `Not yet. ${expected}.` };
}

// ---------------------------------------------------------------------------
// Model boundaries and helpers
// ---------------------------------------------------------------------------

export const MODEL_BOUNDARIES: string[] = [
  'Heat loss: the model assumes every joule released or absorbed changes the water temperature. Real calorimeters lose or gain energy, so experimental |ΔH| is usually smaller than the reference.',
  'Incomplete combustion: the reference values assume complete combustion to CO₂ and liquid water. A sooty or yellow flame means some carbon or CO forms and less energy is released.',
  'Standard conditions: reference ΔH values apply at 25 °C and 100 kPa with liquid water as a product. Classroom conditions differ slightly.',
  'Solutions: the model uses c = 4.18 J g⁻¹ K⁻¹ and a density of 1.00 g mL⁻¹ for dilute solutions, and ignores the mass of a dissolved solid and the heat capacity of the cup.',
  'Activation energies on the profile are illustrative, chosen by you. Only ΔH comes from reviewed data.',
];

export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Formats to significant figures with a Unicode minus sign. */
export function formatSig(value: number, significantFigures: number): string {
  if (value === 0) return '0';
  const text = Number(value.toPrecision(significantFigures)).toString();
  const decimals = Math.max(
    0,
    significantFigures - 1 - Math.floor(Math.log10(Math.abs(Number(text)))),
  );
  return Number(text).toFixed(decimals).replace('-', '−');
}

/** Like formatSig but always shows the sign (+ or −). */
export function formatSigned(value: number, significantFigures: number): string {
  const text = formatSig(value, significantFigures);
  return value > 0 ? `+${text}` : text;
}

/** Fixed decimals with an explicit sign and a Unicode minus sign. */
export function formatDecimalSigned(value: number, decimals: number): string {
  const text = Math.abs(value).toFixed(decimals);
  return value > 0 ? `+${text}` : value < 0 ? `−${text}` : text;
}
